import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth, defaultPatchQuery } from 'api';
import { addArgsToPath, defaultTransformResponse } from 'common/helpers/v1';
import { DowntimeCategoryItem, ProsealConfiguration, RecipeUpdateItem } from './types';
import { ProsealStatusApiResponse, ProsealStatusManagementTable } from 'types/proseal';

const TAGS = ['RecipiesConfigs'];

export const prosealApi = createApi({
  reducerPath: 'prosealApi', //Key in the Redux store where the API slice's reducer state will be stored//prevents conflicts with other slices or reducers
  baseQuery: baseQueryWithReauth, // Replace '/api' with your actual base URL
  tagTypes: TAGS, //Tags are used to group endpoints together in the generated hooks
  endpoints: (builder) => ({
    getDowntimeData: builder.query({
      query: ({ machineId, ...rest }) => {
        return `/mh/v1/proseal/machines/${machineId ?? ''}/downtime${addArgsToPath(rest)}`;
      }
    }),
    getRecipes: builder.query({
      query: ({ machineId }) => {
        return `/mh/v1/machines/${machineId ?? ''}/proseal-recipes`;
      },
      providesTags: ['ProsealRecipes'],
      transformResponse: defaultTransformResponse
    }),
    updateRecipe: builder.mutation<void, RecipeUpdateItem>({
      query: (formData) => {
        const { recipeId, targetPacksPerMinute, color } = formData;
        return defaultPatchQuery(
          { targetPacksPerMinute, color },
          `/mh/v1/proseal-recipe/${recipeId}`
        );
      },
      invalidatesTags: ['ProsealRecipes'],
      transformResponse: defaultTransformResponse
    }),
    getProsealStatusManagementTable: builder.query<
      ProsealStatusApiResponse,
      { limit: number; offset: number; search: string }
    >({
      query: (args) => {
        return `mh/v1/proseal-statuses${addArgsToPath(args)}`;
      },
      providesTags: ['StatusManagementTable'],
      transformResponse: defaultTransformResponse
    }),
    updateProsealStatusManagementTable: builder.mutation<void, ProsealStatusManagementTable>({
      query: (ProsealStatusData: ProsealStatusManagementTable) =>
        defaultPatchQuery(ProsealStatusData, `/mh/v1/proseal-status/${ProsealStatusData.id}`),
      invalidatesTags: ['StatusManagementTable'],
      transformResponse: defaultTransformResponse
    }),
    getDowntimeCategories: builder.query<DowntimeCategoryItem[], void>({
      query: () => {
        return `/mh/v1/proseal-status-categories`;
      },
      transformResponse: defaultTransformResponse
    }),
    getOOE: builder.query({
      query: ({ machineId, ...rest }) => {
        return `/mh/v1/proseal/machines/${machineId ?? ''}/oee${addArgsToPath(rest)}`;
      },
      transformResponse: defaultTransformResponse
    }),
    getProsealConfigurationByMachineId: builder.query<
      ProsealConfiguration[],
      { machineId: string }
    >({
      query: ({ machineId }: { machineId: string }) => {
        return `/mh/v1/machines/${machineId}/proseal-configuration`;
      },
      providesTags: ['ProsealConfiguration'],
      transformResponse: defaultTransformResponse
    }),
    updateProsealMachineConfigurationById: builder.mutation<void, ProsealConfiguration>({
      query: (ProsealConfiguration: ProsealConfiguration) =>
        defaultPatchQuery(
          ProsealConfiguration,
          `/mh/v1/proseal-configuration/${ProsealConfiguration.machineId}`
        ),
      invalidatesTags: ['ProsealConfiguration'],
      transformResponse: defaultTransformResponse
    }),
    getProsealMachineStatus: builder.query({
      query: ({ machineId }) => {
        return `/mh/v1/proseal/machines/${machineId ?? ''}/connection-info`;
      },
      providesTags: ['ProsealRecipes'],
      transformResponse: defaultTransformResponse
    }), 
  })
});

export const {
  useGetDowntimeDataQuery,
  useGetRecipesQuery,
  useUpdateRecipeMutation,
  useGetProsealStatusManagementTableQuery,
  useUpdateProsealStatusManagementTableMutation,
  useGetDowntimeCategoriesQuery,
  useGetOOEQuery,
  useLazyGetProsealStatusManagementTableQuery,
  useGetProsealConfigurationByMachineIdQuery,
  useUpdateProsealMachineConfigurationByIdMutation,
  useGetProsealMachineStatusQuery
} = prosealApi;
